import React, { useState, useEffect } from "react";
import {
  Form,
  Col,
  Container,
  Dropdown,
  DropdownButton,
  Row,
} from "react-bootstrap";
import Select from "react-select";
import DataTable from "react-data-table-component";
import { REPORTS } from "../../configurations/Urls";
import WellReportService from "../../api-service/WellReportService";
import SessionHelper from "../../helpers/SessionHelper";
import { debounce } from "lodash";
import StorageHelper from "../../helpers/StorageHelper";
import DashboardHelper from "../../helpers/DashboardHelper";
import SuperUserService from "../../api-service/SuperUserService";
import LoadingOverlay from "../common/LoadingOverlay";
import MessageBanner from "../common/MessageBanner";
import {
  ERROR_DOWNLOADING_WELL_REPORT,
  ERROR_FETCHING_FARMER_LIST,
  INCOMING_TOKEN_HAS_EXPIRED,
  STAFF_AUTHORIZED,
} from "../../configurations/MessageConstants";
import { EMPTY, STAFF_SESSION_EXPIRED } from "../../configurations/Constants";
import { StatusCodes } from "../../configurations/status-codes";

const SearchBox = ({ onSearch, value }) => (
  <Form.Control
    type="text"
    defaultValue={value}
    name="searchValue"
    onChange={onSearch}
    placeholder="Search"
    className="form-control form-control-lg me-2 w-50"
  />
);

const Export = ({ onExport }) => (
  <DropdownButton
    id="dropdown-basic-button"
    title={
      <span>
        Export <i className="fa fa-download ms-2" aria-hidden="true"></i>
      </span>
    }
  >
    <Dropdown.Item onClick={() => onExport("pdf")}>Export as PDF</Dropdown.Item>
    <Dropdown.Item onClick={() => onExport("xlsx")}>
      Export as XLSX
    </Dropdown.Item>
  </DropdownButton>
);

function FieldWellCropReport(props) {
  const yearArray = DashboardHelper.setYearDataArray([]);
  const [searchValue, setSearchValue] = useState(null);
  const [column, setColumn] = useState(null);
  const [columnOrder, setColumnOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [exportURL, setExportURL] = useState(EMPTY);
  const [overlayLoading, setOverlayLoading] = useState(false);
  const [data, setData] = useState([]);

  const [farmersList, setFarmersList] = useState([]);
  const [farmer, setFarmer] = useState(null);
  const [selectedYear, setSelectedYear] = useState(yearArray[0]);
  const [noCrops, setNoCrops] = useState(true);

  const columns = [
    {
      id: 1,
      name: "Name",
      selector: (row) => row.user_name,
      sortable: true,
      reorder: true,
      value: "user_name",
    },
    {
      id: 2,
      name: "Field Name",
      selector: (row) => row.field_name,
      sortable: true,
      reorder: true,
      value: "field_name",
    },
    {
      id: 3,
      name: "Well",
      selector: (row) => row.wells,
      sortable: true,
      reorder: true,
      value: "wells",
    },
    {
      id: 4,
      name: "Crop",
      selector: (row) => row.crop,
      sortable: true,
      reorder: true,
      value: "crop",
    },
    {
      id: 5,
      name: "Tillage",
      selector: (row) => row.tillage,
      sortable: true,
      reorder: true,
      value: "tillage",
    },
  ];

  useEffect(() => {
    (async function () {
      try {
        const { data } = await SuperUserService.getFarmerList();
        setFarmersList(data.data.users);
      } catch (error) {
        console.error(ERROR_FETCHING_FARMER_LIST, error);
      }
    })();
  }, []);

  useEffect(() => {
    setLoading(true);
    fetchFieldWellCropReports(
      currentPage,
      perPage,
      selectedYear,
      farmer,
      noCrops,
      searchValue,
      column,
      columnOrder
    );
  }, [
    currentPage,
    perPage,
    selectedYear,
    farmer,
    noCrops,
    searchValue,
    column,
    columnOrder,
  ]);

  const fetchFieldWellCropReports = async (
    page,
    size,
    selectedYear,
    farmer,
    noCrops,
    searchValue,
    column,
    columnOrder
  ) => {
    setLoading(true);
    let params = `?current_pg=${page}&rows_per_pg=${size}&crop=${
      noCrops ? "no" : "yes"
    }`;
    if (selectedYear) params = `${params}&year=${selectedYear.value}`;
    if (farmer) params = `${params}&user=${farmer.value}`;
    if (searchValue) params = `${params}&search_text=${searchValue}`;
    if (column !== null) params = `${params}&column=${column}`;
    if (columnOrder !== null) params = `${params}&column_order=${columnOrder}`;

    const url = `${REPORTS}/field-well-crops${params}`;
    const exporturl = `${REPORTS}/field-well-crops/export${params}`;

    try {
      const response = await WellReportService.getFieldWellCropReports(url);
      let reportData = response.data.data;
      if (reportData) {
        setExportURL(exporturl);
        setData(reportData.data);
        setTotalRows(reportData.totalRows);
        setLoading(false);
      }
    } catch (error) {
      const responseData = error.response.data;
      if (
        error.response.status === StatusCodes.UNAUTHORIZED &&
        responseData.message.includes(INCOMING_TOKEN_HAS_EXPIRED)
      ) {
        StorageHelper.setItem(STAFF_SESSION_EXPIRED, true);
        SessionHelper.cleanUpStorageKeys();
        window.location.reload();
      }
    }
  };

  const handleSearchValue = async (e) => {
    await debouncedSearch(e.target.value);
  };

  const debouncedSearch = debounce(async (value) => {
    setSearchValue(value);
  }, 400);

  const handleSort = async (column, sortDirection) => {
    setColumn(column.value);
    setColumnOrder(sortDirection);
  };

  const downloadCSV = async (url) => {
    setOverlayLoading(true);
    try {
      const response = await WellReportService.exportReports(url);
      const filenameRegex = /filename="(.*)"/;
      const matches = filenameRegex.exec(
        response.headers["content-disposition"]
      );
      const fileName =
        matches && matches[1] ? matches[1] : "field_well_crop.xlsx";
      let blob = await response.data;
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = fileName;
      link.click();
      setOverlayLoading(false);
    } catch (error) {
      setOverlayLoading(false);
      const responseData = error.response.data;
      if (
        error.response.status === StatusCodes.UNAUTHORIZED &&
        responseData.message.includes(INCOMING_TOKEN_HAS_EXPIRED)
      ) {
        StorageHelper.setItem(STAFF_SESSION_EXPIRED, true);
        SessionHelper.cleanUpStorageKeys();
        window.location.reload();
      }
      console.error(ERROR_DOWNLOADING_WELL_REPORT, error);
    }
  };

  const actions = (
    <div className="d-flex justify-content-end flex-grow-1 ms-0 field-well-crop-report-actions gap-2">
      <SearchBox onSearch={(e) => handleSearchValue(e, searchValue)} />
      <Export onExport={(item) => downloadCSV(`${exportURL}&type=${item}`)} />
    </div>
  );

  return (
    <div className="field-well-crop-report">
      <LoadingOverlay
        showSpinner={true}
        active={overlayLoading}
        text="Exporting Data..."
      />
      <MessageBanner
        title={"Field Well Crop Report"}
        description={STAFF_AUTHORIZED}
      />
      <Container className="p-3 staff-report-body">
        <Row>
          <Form.Group as={Col} controlId="FormYearList" md="4">
            <Select
              name="yearList"
              options={yearArray}
              value={selectedYear}
              className="basic-multi-select"
              onChange={setSelectedYear}
              placeholder={"Select Tillage"}
            />
          </Form.Group>
          <Form.Group as={Col} md="6" controlId="formFarmerSelect">
            <Select
              name="farmerData"
              options={farmersList}
              isClearable={true}
              className="basic-multi-select"
              onChange={setFarmer}
              value={farmer}
              placeholder="Select a farmer"
            />
          </Form.Group>
          <Form.Group as={Col} md="2" className="mt-2">
            <Form.Check
              type="switch"
              label="No Crops"
              checked={noCrops}
              onChange={() => setNoCrops(!noCrops)}
            />
          </Form.Group>
        </Row>
        <Row>
          <DataTable
            title="Field Well Crop Report"
            columns={columns}
            data={data}
            actions={actions}
            progressPending={loading}
            pagination
            paginationServer
            sortServer
            onSort={handleSort}
            paginationTotalRows={totalRows}
            paginationDefaultPage={currentPage}
            onChangeRowsPerPage={setPerPage}
            onChangePage={setCurrentPage}
            paginationRowsPerPageOptions={[10, 25, 50, 100]}
          />
        </Row>
      </Container>
    </div>
  );
}

export default FieldWellCropReport;
